import React, { useEffect, useState } from 'react';
import API from '../../services/api';
import AnnouncementEditor from '../../components/admin/AnnouncementEditor';
import PaginationControls from '../../components/ui/PaginationControls';
import { usePopup } from '../../context/PopupContext';
import './Analytics.css';

const PAGE_SIZE = 20;

const CATEGORY_LABEL = {
  general: 'General', assessment: 'Assessment', placement_drive: 'Placement Drive',
  company: 'Company Update', deadline: 'Deadline',
};

// Admin authoring for the student Placement Hub feed, targetable by department and/or batch. Students stay
// read-only consumers — this is the only place these rows get created, edited, or removed. (Updates the
// PLATFORM sends to admins live on the separate Platform Updates page.)
const Announcements = () => {
  const { showConfirm } = usePopup();
  const [items, setItems] = useState(null);
  const [batches, setBatches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | announcement object
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Server-side pagination (audit SA-08): this list has no natural size cap (it only ever grows).
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });

  const load = (p = page) => {
    setLoading(true);
    Promise.allSettled([
      API.get('/announcements', { params: { page: p, limit: PAGE_SIZE } }),
      API.get('/batches'),
      API.get('/batches/summary'),
    ]).then(([annRes, batchRes, sumRes]) => {
      if (annRes.status === 'fulfilled') { setItems(annRes.value.data.data); setMeta(annRes.value.data.metadata || { total: 0, total_pages: 1 }); }
      else setErr(annRes.reason?.response?.data?.message || 'Failed to load announcements');
      // /batches returns { id, batch_name, student_count } — the editor speaks { id, name }.
      if (batchRes.status === 'fulfilled') setBatches((batchRes.value.data || []).map(b => ({ id: b.id, name: b.batch_name, student_count: b.student_count })));
      if (sumRes.status === 'fulfilled') setDepartments((sumRes.value.data?.by_branch || []).filter(d => d.branch !== 'Unassigned'));
    }).finally(() => setLoading(false));
  };

  useEffect(() => load(page), [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (payload) => {
    if (editing === 'new') await API.post('/announcements', payload);
    else await API.put(`/announcements/${editing.id}`, payload);
    setEditing(null);
    load();
  };

  const remove = async (a) => {
    if (!(await showConfirm('Delete Announcement', `Delete "${a.title}"? This removes it from students' Placement Hub immediately.`))) return;
    await API.delete(`/announcements/${a.id}`);
    load();
  };

  if (loading) return <div className="axh-page"><div className="axh-loading"><div className="axh-spinner" />Loading announcements…</div></div>;

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div><h1>Student Announcements</h1><p>What students see on their Placement Hub — target the whole college, a department, a batch, or both.</p></div>
        <div className="axh-topbar-right">
          <button type="button" className="axh-btn primary" aria-expanded={editing === 'new'} onClick={() => setEditing(cur => (cur === 'new' ? null : 'new'))}>
            {editing === 'new' ? 'Close form' : '+ New Announcement'}
          </button>
        </div>
      </div>

      {editing && (
        <div className="axh-card" style={{ marginBottom: 20 }}>
          <h3 aria-level={2}>{editing === 'new' ? 'New Announcement' : 'Edit Announcement'}</h3>
          <AnnouncementEditor
            key={editing === 'new' ? 'new' : editing.id}
            initial={editing === 'new' ? null : editing}
            batches={batches}
            departments={departments}
            onSave={save}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      {err && <div className="axh-error">{err}</div>}

      <div className="axh-section">
        {(items || []).length === 0 ? (
          <div className="axh-card"><div className="axh-empty">No announcements yet — create one above to publish it to students.</div></div>
        ) : (
          <div className="axh-grid-2">
            {items.map(a => (
              <div className="axh-card" key={a.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <span className="axh-pill" style={{ background: 'var(--z-info-tint)', color: 'var(--z-violet)' }}>{CATEGORY_LABEL[a.category] || a.category}</span>
                  {a.pinned && <span className="axh-badge" style={{ background: 'var(--z-warning-tint)', color: 'var(--z-warning)' }}>Pinned</span>}
                </div>
                <h3 aria-level={2} style={{ marginBottom: 6 }}>{a.title}</h3>
                <p style={{ fontSize: '0.84rem', color: 'var(--z-muted)', marginBottom: 12 }}>{a.body}</p>
                <p style={{ fontSize: '0.74rem', color: 'var(--z-muted-2)', marginBottom: 12 }}>
                  {a.audience || 'Whole college'}
                  {a.expires_at ? ` · Expires ${new Date(a.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}` : ''}
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="axh-btn" onClick={() => setEditing(a)}>Edit</button>
                  <button type="button" className="axh-btn" style={{ color: 'var(--z-danger)' }} onClick={() => remove(a)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <PaginationControls page={page} totalPages={meta.total_pages} total={meta.total} onChange={setPage} />
      </div>
    </div>
  );
};

export default Announcements;
